import "./campaign-response-row.style.scss";
import { Col } from "@amzn/stencil-react-components/layout";
import { Text, TextVariant } from "@amzn/stencil-react-components/text";
import { Select } from "@amzn/stencil-react-components/select";
import { Input, InputFooter } from "@amzn/stencil-react-components/input";
import { ScreenReaderOnly } from "@amzn/stencil-react-components/screen-reader-only";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import React, { FC, useId } from "react";
import { RESPONSE_COLUMN_LABEL_KEYS } from "../campaigns.constants";
import { ResponseCellLabel, ResponseGridRow } from "./campaign-response-grid";
import { RESPONSE_OPTIONS } from "../response-options";
import type { CampaignResponseOption, CampaignResponseValue } from "../response-options";
import type { Asset } from "@amzn/hardware-self-service-api-model";

const RESPONSE_OPTIONS_DATA_ATTRIBUTE = "data-hss-response-options";

interface ResponseOptionListItemProps {
  option: CampaignResponseOption;
}

const ResponseOptionListItem: FC<ResponseOptionListItemProps> = ({ option }) => (
  <Col gridGap="S100" padding={["S100", "S200"]} alignItems="flex-start">
    <Text variant={TextVariant.LabelMD}>{option.label}</Text>
    {option.description && (
      <Text variant={TextVariant.LabelXS} fontStyle="italic" color="neutral70">
        {option.description}
      </Text>
    )}
  </Col>
);

const renderResponseOption = (option: CampaignResponseOption) => (
  <ResponseOptionListItem option={option} />
);

const renderResponseLabel = (option: CampaignResponseOption) => option.label;

interface CampaignResponseRowProps {
  asset: Asset;
  value: CampaignResponseValue;
  remarks: string;
  isRemarksMissing?: boolean;
  disabled?: boolean;
  onResponseChange: (value: CampaignResponseValue) => void;
  onRemarksChange: (value: string) => void;
}

function getDeviceName(asset: Asset): string {
  return asset.model ?? getString("campaigns.unknownDevice");
}

export const CampaignResponseRow: FC<CampaignResponseRowProps> = ({
  asset,
  value,
  remarks,
  isRemarksMissing = false,
  disabled = false,
  onResponseChange,
  onRemarksChange,
}) => {
  const selectedOption = RESPONSE_OPTIONS.find((option) => option.value === value);
  const serialNumber = asset.serialNumber ?? getString("campaigns.unknown");
  const deviceStatusLabelId = useId();
  const remarksErrorId = useId();

  return (
    <ResponseGridRow role="row">
      <Col role="cell">
        <ResponseCellLabel>
          <Text fontWeight="bold">{getString(RESPONSE_COLUMN_LABEL_KEYS.serialNumber)}</Text>
        </ResponseCellLabel>
        <Text>{serialNumber}</Text>
      </Col>
      <Col role="cell">
        <ResponseCellLabel>
          <Text fontWeight="bold">{getString(RESPONSE_COLUMN_LABEL_KEYS.model)}</Text>
        </ResponseCellLabel>
        <Text>{getDeviceName(asset)}</Text>
      </Col>
      <Col role="cell">
        <ResponseCellLabel aria-hidden="true">
          <Text fontWeight="bold">{getString(RESPONSE_COLUMN_LABEL_KEYS.deviceStatus)}</Text>
        </ResponseCellLabel>
        <ScreenReaderOnly id={deviceStatusLabelId}>
          {getString("campaigns.deviceStatusLabel", { serialNumber })}
        </ScreenReaderOnly>
        <Select
          labelId={deviceStatusLabelId}
          dataTestId={`response-${serialNumber}`}
          value={selectedOption}
          onChange={(option: CampaignResponseOption) => onResponseChange(option.value)}
          options={RESPONSE_OPTIONS}
          valueAccessor="value"
          renderOption={renderResponseOption}
          renderNativeOption={renderResponseLabel}
          renderSelectedValue={renderResponseLabel}
          placeholder={getString("campaigns.selectResponse")}
          width="100%"
          listPositioningStrategy="fixed"
          disabled={disabled}
          slotProps={{ optionList: { [RESPONSE_OPTIONS_DATA_ATTRIBUTE]: "true" } }}
        />
      </Col>
      <Col role="cell">
        <ResponseCellLabel aria-hidden="true">
          <Text fontWeight="bold">{getString(RESPONSE_COLUMN_LABEL_KEYS.remarks)}</Text>
        </ResponseCellLabel>
        <Input
          id={`remarks-${serialNumber}`}
          dataTestId={`remarks-${serialNumber}`}
          placeholder={getString("campaigns.remarksPlaceholder")}
          aria-label={getString("campaigns.remarksLabel", { serialNumber })}
          value={remarks}
          disabled={disabled}
          error={isRemarksMissing}
          aria-invalid={isRemarksMissing}
          aria-describedby={isRemarksMissing ? remarksErrorId : undefined}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            onRemarksChange(event.target.value)
          }
        />
        {isRemarksMissing && (
          <InputFooter id={remarksErrorId} error>
            {getString("campaigns.remarksRequiredLostOrStolen")}
          </InputFooter>
        )}
      </Col>
    </ResponseGridRow>
  );
};
